import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { getSettings, adminUpdateSettings } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const FIELDS = [
  { key: "business_name", label: "Business Name", type: "text" },
  { key: "tagline", label: "Tagline", type: "text" },
  { key: "whatsapp_number", label: "WhatsApp Number (digits, with country code)", type: "text" },
  { key: "phone_number", label: "Phone Number (digits, with country code)", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "hero_title", label: "Homepage Hero Title", type: "textarea" },
  { key: "hero_subtitle", label: "Homepage Hero Subtitle", type: "textarea" },
  { key: "about", label: "About Text", type: "textarea" },
];

const SettingsPage = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings(form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="font-heading text-4xl text-secondary">Site Settings</h1>
      <p className="text-muted-foreground mt-1">Update contact details and homepage content.</p>
      <div className="mt-8 space-y-4 bg-white border border-border rounded-xl p-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-sm">{f.label}</Label>
            {f.type === "textarea"
              ? <Textarea className="mt-1" rows={3} value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`settings-${f.key}`} />
              : <Input className="mt-1" value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`settings-${f.key}`} />}
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid="save-settings" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204] disabled:opacity-60"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
};

export default SettingsPage;
